import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, UserPlus } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { EmployeeForm } from "./EmployeeForm";

export const EmployeesPanel = ({ query }) => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/employees");
      setList(Array.isArray(data) ? data : []);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  useEffect(() => {
    load();
    return undefined;
  }, [load]);

  const create = async (body) => {
    try {
      const { data } = await api.post("/admin/employees", body);
      setCreated({ code: data.emp_code, password: body.password, name: data.name });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const term = query.trim().toLowerCase();
  const items = term ? list.filter((e) => JSON.stringify(e).toLowerCase().includes(term)) : list;

  return (
    <div data-testid="employees-panel">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-[var(--bone-2)]">{list.length} employees</p>
        <button onClick={() => { setCreated(null); setOpen(true); }} data-testid="add-employee-button"
          className="flex items-center gap-2 rounded-full bg-[var(--signal)] px-4 py-2 text-sm font-semibold text-[var(--ink)]">
          <UserPlus className="h-4 w-4" /> Add employee
        </button>
      </div>
      {items.length ? (
        <ul className="divide-y divide-white/10 border-y border-white/10" data-testid="employees-list">
          {items.map((e) => (
            <li key={e.id}>
              <Link to={`/admin/employees/${e.id}`} data-testid={`employee-row-${e.emp_code}`}
                className="group grid gap-2 px-2 py-4 transition-colors hover:bg-white/[0.03] md:grid-cols-[110px_1.3fr_1fr_auto_auto] md:items-center md:gap-6">
                <span className="font-mono text-sm text-[var(--signal)]">{e.emp_code}</span>
                <span className="font-semibold">{e.name}</span>
                <span className="text-sm text-[var(--bone-2)]">{[e.designation, e.site].filter(Boolean).join(" · ") || "—"}</span>
                <span className={`w-fit rounded-full border px-2.5 py-0.5 font-mono text-[11px] uppercase ${e.active ? "border-emerald-400/60 text-emerald-300" : "border-white/25 text-[var(--bone-2)]"}`}>
                  {e.active ? "Active" : "Inactive"}
                </span>
                <ArrowUpRight className="hidden h-4 w-4 transition-transform group-hover:rotate-45 md:block" />
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="border border-dashed border-white/15 p-10 text-center text-[var(--bone-2)]" data-testid="employees-empty">No employees yet. Add your first one.</p>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-lenis-prevent className="max-h-[90vh] overflow-y-auto border-white/15 bg-[var(--ink-2)] text-[var(--bone)]" data-testid="add-employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-4xl font-black uppercase">{created ? "Employee created" : "Add employee"}</DialogTitle>
            <DialogDescription className="text-[var(--bone-2)]">
              {created ? "Share these login details with the employee. The password is not shown again." : "An employee ID is generated automatically."}
            </DialogDescription>
          </DialogHeader>
          {created ? (
            <div className="space-y-3 border border-[var(--signal)] p-5" data-testid="employee-created-credentials">
              <p><span className="overline text-[var(--bone-2)]">Name</span><br />{created.name}</p>
              <p><span className="overline text-[var(--bone-2)]">Employee ID</span><br /><span className="font-mono text-xl text-[var(--signal)]" data-testid="created-emp-code">{created.code}</span></p>
              <p><span className="overline text-[var(--bone-2)]">Password</span><br /><span className="font-mono">{created.password}</span></p>
              <p className="text-sm text-[var(--bone-2)]">Login page: {window.location.origin}/employee/login</p>
            </div>
          ) : (
            <EmployeeForm onSubmit={create} submitLabel="Create employee" passwordRequired />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
